// Single source of truth for turning a LiteAPI rate object into the numbers
// we show the user. Any place that displays a per-night or total price must
// import this — otherwise the ResultsPage number won't match the PropertyPage
// number and the carry-over will look broken.

export function computeRate(rate, stayNights) {
  const retail = rate?.retailRate || {};
  const base = Number(retail.total?.[0]?.amount ?? retail.total?.amount) || 0;
  const commission = Array.isArray(rate?.commission)
    ? Number(rate.commission[0]?.amount) || 0
    : Number(rate?.commission?.amount) || 0;
  let inc = 0, exc = 0;
  for (const fee of retail.taxesAndFees || []) {
    if (fee?.included) inc += Number(fee.amount) || 0;
    else exc += Number(fee.amount) || 0;
  }
  // LiteAPI sometimes reports the whole base as "included fees" (bad data).
  // Guard against that so we don't zero out the whole rate.
  if (inc > 0.8 * base) inc = 0;
  const nights = stayNights || 1;
  return {
    baseRate: base,
    commission,
    feesIncluded: inc,
    feesExcluded: exc,
    totalPrice: Math.max(0, Math.round(base + exc)),
    nightly:    Math.max(0, Math.round((base - commission - inc) / nights))
  };
}

export const round2 = n => Math.round((Number(n) || 0) * 100) / 100;

// Google's PriceComponentTypeEnumeration for a fee/tax label. Anything that
// isn't a tax, resort fee or channel service fee is left untyped (name only) —
// e.g. "Cleaning fee" has no enumeration value and mislabelling it is worse.
export function feeComponentType(label) {
  const lower = String(label || "").toLowerCase();
  if (/resort/.test(lower)) return "ResortFee";
  if (/service|booking fee|channel/.test(lower)) return "ServiceFee";
  if (/tax|vat|gst|hst|tourism|levy/.test(lower)) return "GenericTax";
  if (/transfer|shuttle/.test(lower)) return "TransferFee";
  return undefined;
}

// Collapse LiteAPI's taxesAndFees rows into the labelled lines we show the
// guest ("City tax", "TAX", "local city tax" → one "Taxes" row). Included and
// excluded rows are grouped separately so the checkout math stays exact.
// Returns [{ description, amount, included, componentType }].
export function groupTaxesAndFees(rows) {
  const buckets = new Map();
  for (const t of rows || []) {
    const label = (t?.description || "").trim();
    const lower = label.toLowerCase();
    let group;
    if (/resort/.test(lower))               group = "Resort fee";
    else if (/tourism/.test(lower))         group = "Tourism fee";
    else if (/service/.test(lower))         group = "Service fee";
    else if (/cleaning/.test(lower))        group = "Cleaning fee";
    else if (/tax|vat|gst|hst/.test(lower)) group = "Taxes";
    else group = label || "Fees";
    const included = !!t?.included;
    const key = `${included ? "i" : "e"}:${group}`;
    const cur = buckets.get(key) || { description: group, amount: 0, included, componentType: feeComponentType(group) };
    cur.amount += Number(t?.amount) || 0;
    buckets.set(key, cur);
  }
  return [...buckets.values()].map(l => ({ ...l, amount: round2(l.amount) }));
}

// Itemised breakdown for structured data, reconciled so the lines sum to the
// exact total shown on screen. `payToday` is the retail total charged at
// booking; `feesIncluded` is the (guarded) included-tax sum from computeRate —
// when the guard zeroed it we don't itemise included taxes either, so markup
// and display agree. Excluded rows are collected at the property.
export function priceBreakdown({ payToday, feesIncluded, taxesAndFees, total, currency = "USD" }) {
  const groups = groupTaxesAndFees(taxesAndFees);
  const included = feesIncluded > 0 ? groups.filter(g => g.included) : [];
  const excluded = groups.filter(g => !g.included);
  const includedSum = included.reduce((s, g) => s + g.amount, 0);
  const lines = [
    { name: "Base rate", amount: round2(payToday - includedSum), atProperty: false },
    ...included.map(g => ({ name: g.description, amount: g.amount, componentType: g.componentType, atProperty: false })),
    ...excluded.map(g => ({ name: g.description, amount: g.amount, componentType: g.componentType, atProperty: true }))
  ];
  const sum = lines.reduce((s, l) => s + l.amount, 0);
  const diff = round2(total - sum);
  // Display totals are rounded to whole dollars; fold the sub-dollar rounding
  // into the base rate so the components add up to the advertised price.
  if (diff !== 0 && Math.abs(diff) < 1) lines[0].amount = round2(lines[0].amount + diff);
  return { total: round2(total), currency, lines };
}
